import React, { useState } from 'react'
import domPurify from 'dompurify'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  TextField,
} from '@mui/material'
import { Button, colors } from '@resiliatech/birdsong'
import {
  ButtonAction,
  RadioAction,
  SelectAction,
  CustomAction,
  TextFieldAction,
  CheckboxAction,
} from '../types'
import { ActionElementType } from '../constants'

type ActionElementProps = {
  action: CustomAction
  onAction: ({ nextStep, callback }: any) => void // TODO: Type this properly
  saveUserState?: boolean
}

export const ActionElement = ({
  action,
  onAction,
  saveUserState = false,
}: ActionElementProps) => {
  const { type } = action
  const [isSelectOpen, setIsSelectOpen] = useState(false)
  const requiredDefault = false

  switch (type) {
    case ActionElementType.BUTTON: {
      const { callback, disabled, label, path } = action as ButtonAction
      return (
        <Box>
          <Button
            isDisabled={disabled}
            label={label}
            onClick={() =>
              onAction({
                nextStep: path,
                callback: () => callback?.(),
                saveUserState,
              })
            }
            variant={(action as ButtonAction).variant || 'primary'}
          />
        </Box>
      )
    }
    case ActionElementType.CHECKBOX: {
      const { callback, checked, label, styles } = action as CheckboxAction
      return (
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={() =>
                  onAction({ nextStep: undefined, callback, saveUserState })
                }
                sx={styles}
              />
            }
            label={label}
          />
        </Box>
      )
    }
    case ActionElementType.RADIO: {
      const { callback, checkValue, label, required, value } =
        action as RadioAction

      return (
        <Box>
          <FormControlLabel
            value={value}
            control={
              <Radio
                checked={!!checkValue && checkValue === value}
                onChange={() =>
                  onAction({ nextStep: undefined, callback, saveUserState })
                }
                required={required || requiredDefault}
                value={value}
              />
            }
            label={label}
          />
        </Box>
      )
    }
    case ActionElementType.SELECT: {
      const { callback, label, options, required, value } =
        action as SelectAction

      return (
        <Box>
          <FormControl required={required || requiredDefault} fullWidth>
            <InputLabel id={`${label}-select`}>{label}</InputLabel>
            <Select
              labelId={`${label}-select`}
              value={value}
              label={label}
              onChange={(e) =>
                onAction({
                  nextStep: undefined,
                  callback: () => callback?.(e.target.value),
                  saveUserState,
                })
              }
              onOpen={() => setIsSelectOpen(true)}
              onClose={() => setIsSelectOpen(false)}
            >
              {!isSelectOpen && <MenuItem value='' />}
              {options.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={
                    option.showDivider
                      ? { borderTop: `1px solid ${colors.divider}` }
                      : {}
                  }
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )
    }
    case ActionElementType.TEXT_FIELD: {
      const { callback, label, multiline, required, value } =
        action as TextFieldAction

      return (
        <Box>
          <TextField
            multiline={!!multiline}
            label={label}
            onChange={(e) =>
              onAction({
                nextStep: undefined,
                callback: () => callback?.(e.target.value),
                saveUserState,
              })
            }
            required={required || requiredDefault}
            value={domPurify.sanitize(value)}
            sx={{ width: '100%' }}
          />
        </Box>
      )
    }
    default:
      return null
  }
}
